import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ChevronRight, Crosshair, Shield, Zap } from 'lucide-react';

const SUBSCRIPTION_URL = "https://elev802boston.playbookapi.com/subscribe/subscription_registration/?subscription_id=4447";
const POSITION_LABELS = { forward: 'Forward', defense: 'Defense', goalie: 'Goalie' };
const SKILL_LABELS = { aa_house: 'AA', aaa_travel: 'AAA', elite: 'Elite' };

export default function PlanReady() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState('loading'); // loading → reveal

  const position = user?.profile?.position || 'forward';
  const skill = user?.profile?.skill_level || 'aa_house';
  const posLabel = POSITION_LABELS[position] || position;
  const skillLabel = SKILL_LABELS[skill] || skill;
  const planTag = `${skillLabel} ${posLabel} Plan`;

  useEffect(() => {
    const timer = setTimeout(() => setPhase('reveal'), 2400);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center px-6">
      <AnimatePresence mode="wait">
        {phase === 'loading' && (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="text-center"
          >
            {/* Animated rings */}
            <div className="relative w-28 h-28 mx-auto mb-8">
              <motion.div
                className="absolute inset-0 rounded-full border-2 border-[#2563EB]/30"
                animate={{ scale: [1, 1.4, 1], opacity: [0.5, 0, 0.5] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
              />
              <motion.div
                className="absolute inset-2 rounded-full border-2 border-[#00E5FF]/30"
                animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }}
              />
              <div className="absolute inset-4 rounded-full bg-[#2563EB]/15 flex items-center justify-center">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                >
                  <Crosshair size={32} className="text-[#2563EB]" />
                </motion.div>
              </div>
            </div>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold tracking-tight mb-2">
              BUILDING YOUR PLAN
            </h2>
            <p className="text-[#64748B] text-sm">
              Analyzing your position, skill level, and goals...
            </p>
          </motion.div>
        )}

        {phase === 'reveal' && (
          <motion.div
            key="reveal"
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="text-center max-w-sm w-full"
          >
            {/* Success icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
              className="w-20 h-20 rounded-2xl bg-[#10B981]/15 border border-[#10B981]/30 flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle2 size={40} className="text-[#10B981]" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <h1 className="font-['Barlow_Condensed'] text-3xl font-black tracking-tight mb-2" data-testid="plan-ready-title">
                YOUR PLAN IS <span className="text-[#00E5FF]">READY</span>
              </h1>
              <p className="text-[#64748B] text-sm mb-6">
                Built for your position, skill level, and goals
              </p>
            </motion.div>

            {/* Player Tag */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
              className="mb-8"
            >
              <div className="inline-flex items-center gap-2 bg-white border border-[#2563EB]/40 rounded-xl px-5 py-3" data-testid="player-tag">
                <Shield size={16} className="text-[#2563EB]" />
                <span className="font-['Barlow_Condensed'] text-base font-bold tracking-tight text-[#0A2242]">
                  {planTag}
                </span>
              </div>
            </motion.div>

            {/* Plan preview cards */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="bg-white border border-[#E2E8F0] rounded-xl p-5 mb-8 text-left"
            >
              <h3 className="font-['Barlow_Condensed'] text-sm font-bold text-[#64748B] uppercase tracking-widest mb-3">
                Week 1 Includes
              </h3>
              <div className="space-y-2.5">
                {[
                  '5 structured training days (Mon–Fri)',
                  'Position-specific drills with coaching cues',
                  'Mental preparation tips each day',
                  'Nutrition guidance for peak performance',
                  'Progress tracking & streaks',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <Zap size={12} className="text-[#00E5FF] flex-shrink-0" />
                    <span className="text-sm text-[#64748B]">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
              className="space-y-3"
            >
              {user?.subscription_active ? (
                <button
                  onClick={() => navigate('/dashboard', { replace: true })}
                  className="w-full bg-gradient-to-r from-[#2563EB] to-[#00E5FF] text-[#060913] font-black uppercase tracking-wide rounded-lg px-8 py-4 transition-all hover:opacity-90 flex items-center justify-center gap-2 text-base"
                  data-testid="view-my-plan-btn"
                >
                  View My Plan
                  <ChevronRight size={20} />
                </button>
              ) : (
                <>
                  <a
                    href={SUBSCRIPTION_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-gradient-to-r from-[#2563EB] to-[#00E5FF] text-[#060913] font-black uppercase tracking-wide rounded-lg px-8 py-4 transition-all hover:opacity-90 flex items-center justify-center gap-2 text-base"
                    data-testid="view-my-plan-btn"
                  >
                    Activate My Plan — $14.99/mo
                    <ChevronRight size={20} />
                  </a>
                  <button
                    onClick={() => navigate('/dashboard', { replace: true })}
                    className="w-full text-[#64748B] hover:text-[#64748B] text-xs font-medium py-2 transition-colors"
                    data-testid="already-subscribed-btn"
                  >
                    I've already subscribed
                  </button>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
